import type {
	ActionsMeters,
	ApiKeysMeters,
	DashboardMeters,
	DbcMeters,
	DeviceMeters,
	ExportersMeters,
	MetadataMeters,
	MetersModule,
	OrganizationMeters,
	RolesMeters,
	SessionsMeters,
	StreamsMeters,
	UsersMeters,
} from '@/types/organization'
import type {
	ActionsMetricData,
	ActionsMetricsResponse,
	ApiKeysMetricData,
	ApiKeysMetricsResponse,
	DbcMetricsResponse,
	GeneralMetricData,
	GeneralMetricsResponse,
	SessionsMetricsResponse,
	StreamsMetricData,
	StreamsMetricsResponse,
} from '@/types/organization-owner-api'

type ConsumedAllowedMeters =
	| DeviceMeters
	| DashboardMeters
	| UsersMeters
	| RolesMeters
	| MetadataMeters
	| ExportersMeters
	| ApiKeysMeters
	| DbcMeters

function consumedAllowedToMeters<T extends ConsumedAllowedMeters>(r: Record<string, GeneralMetricData>): T[] {
	return Object.entries(r).map(([tenant_id, m]) => ({
		tenant_id,
		meters: { consumed: m.consumed, allowed: m.allowed },
	})) as T[]
}

export function generalToDeviceMeters(r: Record<string, GeneralMetricData>): DeviceMeters[] {
	return consumedAllowedToMeters<DeviceMeters>(r)
}

export function generalToDashboardMeters(r: Record<string, GeneralMetricData>): DashboardMeters[] {
	return consumedAllowedToMeters<DashboardMeters>(r)
}

export function generalToUsersMeters(r: Record<string, GeneralMetricData>): UsersMeters[] {
	return consumedAllowedToMeters<UsersMeters>(r)
}

export function generalToRolesMeters(r: Record<string, GeneralMetricData>): RolesMeters[] {
	return consumedAllowedToMeters<RolesMeters>(r)
}

export function generalToMetadataMeters(r: Record<string, GeneralMetricData>): MetadataMeters[] {
	return consumedAllowedToMeters<MetadataMeters>(r)
}

export function dbcToDbcMeters(r: DbcMetricsResponse): DbcMeters[] {
	return consumedAllowedToMeters<DbcMeters>(r)
}

export function generalToExportersMeters(r: Record<string, GeneralMetricData>): ExportersMeters[] {
	return consumedAllowedToMeters<ExportersMeters>(r)
}

export function apikeysToApiKeysMeters(r: Record<string, ApiKeysMetricData>): ApiKeysMeters[] {
	return consumedAllowedToMeters<ApiKeysMeters>(r)
}

export function actionsToActionsMeters(r: Record<string, ActionsMetricData>): ActionsMeters[] {
	return Object.entries(r).map(([tenant_id, m]) => ({
		tenant_id,
		meters: {
			max_action_types: m.max_action_types,
			max_actions: m.max_actions,
			action_types_consumed: m.action_types_consumed,
			actions: m.actions,
			total_actions: Object.values(m.actions).reduce((a, b) => a + b, 0),
		},
	}))
}

export function streamsToStreamsMeters(r: Record<string, StreamsMetricData>): StreamsMeters[] {
	return Object.entries(r).map(([tenant_id, m]) => ({
		tenant_id,
		meters: {
			consumed: m.consumed,
			allowed: m.allowed,
			disk_space: m.disk_space,
			rows_count: m.rows_count,
			ttl_issues: Array.isArray(m.ttl_issues) ? m.ttl_issues : [],
		},
	}))
}

export function sessionsToSessionsMeters(r: SessionsMetricsResponse): SessionsMeters[] {
	return Object.entries(r).map(([tenant_id, metrics]) => ({
		tenant_id,
		meters: {
			session_rules_consumed: metrics.max_session_rules?.consumed ?? 0,
			session_rules_allowed: metrics.max_session_rules?.allowed ?? 0,
			subscriber_groups_consumed: metrics.max_subscriber_groups?.consumed ?? 0,
			subscriber_groups_allowed: metrics.max_subscriber_groups?.allowed ?? 0,
			alert_rules_consumed: metrics.max_alert_rules?.consumed ?? 0,
			alert_rules_allowed: metrics.max_alert_rules?.allowed ?? 0,
			session_types_consumed: metrics.max_session_types?.consumed ?? 0,
			session_types_allowed: metrics.max_session_types?.allowed ?? 0,
			alert_notification_rules_consumed: metrics.max_alert_notification_rules?.consumed ?? 0,
			alert_notification_rules_allowed: metrics.max_alert_notification_rules?.allowed ?? 0,
		},
	}))
}

export interface OrganizationMetersFetcher {
	getDevicesMeters: (organizationId: string) => Promise<GeneralMetricsResponse>
	getDashboardsMeters: (organizationId: string) => Promise<GeneralMetricsResponse>
	getUsersMeters: (organizationId: string) => Promise<GeneralMetricsResponse>
	getRolesMeters: (organizationId: string) => Promise<GeneralMetricsResponse>
	getMetadataMeters: (organizationId: string) => Promise<GeneralMetricsResponse>
	getApiKeysMeters: (organizationId: string) => Promise<ApiKeysMetricsResponse>
	getActionsMeters: (organizationId: string) => Promise<ActionsMetricsResponse>
	getStreamsMeters: (organizationId: string) => Promise<StreamsMetricsResponse>
	getDbcMeters: (organizationId: string) => Promise<DbcMetricsResponse>
	getExportersMeters: (organizationId: string) => Promise<GeneralMetricsResponse>
	getSessionsMeters: (organizationId: string) => Promise<SessionsMetricsResponse>
}

type MetersFetchKey = keyof OrganizationMetersFetcher

type MetersModuleHandler = {
	[K in MetersFetchKey]: {
		fetch: K
		map: (response: Awaited<ReturnType<OrganizationMetersFetcher[K]>>) => OrganizationMeters[]
	}
}[MetersFetchKey]

export const ORGANIZATION_METERS_MODULE_HANDLERS = {
	devices: { fetch: 'getDevicesMeters', map: generalToDeviceMeters },
	dashboards: { fetch: 'getDashboardsMeters', map: generalToDashboardMeters },
	users: { fetch: 'getUsersMeters', map: generalToUsersMeters },
	roles: { fetch: 'getRolesMeters', map: generalToRolesMeters },
	metadata: { fetch: 'getMetadataMeters', map: generalToMetadataMeters },
	apikeys: { fetch: 'getApiKeysMeters', map: apikeysToApiKeysMeters },
	actions: { fetch: 'getActionsMeters', map: actionsToActionsMeters },
	streams: { fetch: 'getStreamsMeters', map: streamsToStreamsMeters },
	dbc: { fetch: 'getDbcMeters', map: dbcToDbcMeters },
	exporters: { fetch: 'getExportersMeters', map: generalToExportersMeters },
	sessions: { fetch: 'getSessionsMeters', map: sessionsToSessionsMeters },
} as const satisfies Record<MetersModule, MetersModuleHandler>

export async function fetchOrganizationMeters(
	api: OrganizationMetersFetcher,
	organizationId: string,
	module: MetersModule,
): Promise<OrganizationMeters[]> {
	const handler = ORGANIZATION_METERS_MODULE_HANDLERS[module]
	const response = await api[handler.fetch](organizationId)
	return handler.map(response as never)
}
